/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

class WeightRepository {
  // Get all weights
  async findAll() {
    const res = await databaseService.query('SELECT * FROM weights');
    const rows = (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      value: row.value,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));

    return rows.sort((a, b) => new Date(b.measuredAt) - new Date(a.measuredAt));
  }

  /**
   * Get all weights for a goat (latest first)
   * @param {number} goatId
   * @param {number|null} limit
   * @returns {Promise<Array<import('../../models/weight.model.js').Weight>>}
   */
  async findByGoat(goatId, limit = null) {
    const base = 'SELECT * FROM weights WHERE goat_id = ? ORDER BY measured_at DESC';
    const sql = limit ? `${base} LIMIT ?` : base;
    const params = limit ? [goatId, limit] : [goatId];

    const res = await databaseService.query(sql, params);
    return (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      value: row.value,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }

  /**
   * Create a new weight entry
   * @param {{goatId:number,value:number,measuredAt?:string}} payload
   * @returns {Promise<number|null>} last inserted id
   */
  async create({ goatId, value, measuredAt = null }) {
    const sql = measuredAt
      ? 'INSERT INTO weights (goat_id, value, measured_at) VALUES (?, ?, ?)'
      : 'INSERT INTO weights (goat_id, value) VALUES (?, ?)';
    const params = measuredAt ? [goatId, value, measuredAt] : [goatId, value];
    const r = await databaseService.run(sql, params);
    return r?.changes?.lastId ?? null;
  }

  /**
   * Update an existing weight record
   * @param {number} id
   * @param {{value:number,measuredAt?:string}} payload
   */
  async update(id, { value, measuredAt = null }) {
    const sql = measuredAt
      ? 'UPDATE weights SET value = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?'
      : 'UPDATE weights SET value = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?';
    const params = measuredAt ? [value, measuredAt, id] : [value, id];
    await databaseService.run(sql, params);
  }

  /**
   * Delete a weight entry
   * @param {number} id
   */
  async delete(id) {
    await databaseService.run('DELETE FROM weights WHERE id = ?', [id]);
  }

  /**
   * Delete all weight entries for a specific goat
   * @param {number} goatId
   */
  async deleteByGoat(goatId) {
    await databaseService.run('DELETE FROM weights WHERE goat_id = ?', [goatId]);
  }

  /**
   * Get the latest weight for a goat
   */
  async latest(goatId) {
    const rows = await this.findByGoat(goatId, 1);
    return rows[0] ?? null;
  }
}

export const weightRepository = new WeightRepository();
